import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { showsInstructorProfile } from "@lms/backend/lib/tenants/public-profile";
import { Card, Heading, PageShell, Text } from "@/components/ui";
import { buttonClasses } from "@/components/ui/button";
import { fetchTenantCatalog } from "@/services/api/tenants";
import { GUIDANCE_KEYS, INSTRUCTOR_IMAGES } from "@/pages/tenant/instructor-profile-copy";

/**
 * The tenant landing page at `/t/:tenantSlug`.
 *
 * The route is served for every tenant, but the instructor hero,
 * guidance, approach and philosophy sections belong to exactly one of them —
 * `showsInstructorProfile` is the whole of that decision, so a new tenant gets a
 * neutral page built from its own name and catalogue rather than an empty shell
 * where somebody else's biography used to be.
 */
export function TenantHomePage() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const t = useTranslations("tenantPublic");

  const catalogQuery = useQuery({
    queryKey: ["tenant-catalog", tenantSlug],
    queryFn: () => fetchTenantCatalog(tenantSlug),
  });

  if (catalogQuery.isLoading) {
    return (
      <PageShell data-testid="tenant-home-loading">
        <Text tone="muted">{t("loading")}</Text>
      </PageShell>
    );
  }

  if (catalogQuery.isError || !catalogQuery.data) {
    return (
      <PageShell data-testid="tenant-home-error">
        <Heading as="h1" level="page">
          {t("tenantUnavailable")}
        </Heading>
      </PageShell>
    );
  }

  const { tenant, courses } = catalogQuery.data;
  const coursesHref = `/t/${tenantSlug}/courses`;
  const aboutHref = `/t/${tenantSlug}/about`;
  const showsProfile = showsInstructorProfile(tenantSlug);

  const browseCoursesLink = (
    <Link
      to={coursesHref}
      className={buttonClasses({ variant: "accent", size: "lg", pill: true })}
      data-testid="home-browse-courses"
    >
      {t("browseCourses")}
    </Link>
  );

  if (!showsProfile) {
    return (
      <PageShell
        width="app"
        pad="none"
        className="flex min-h-[min(60vh,40rem)] flex-1 flex-col justify-center py-12 sm:py-16"
        data-testid="tenant-home"
      >
        <Heading as="h1" level="display">
          {tenant.name}
        </Heading>
        <Text tone="muted" size="lg" className="mt-4 max-w-xl">
          {t("tenantStandfirst", { tenant: tenant.name })}
        </Text>
        <Text tone="muted" size="sm" className="mt-2">
          {t("courseCount", { count: courses.length })}
        </Text>
        <div className="mt-8">{browseCoursesLink}</div>
      </PageShell>
    );
  }

  return (
    <PageShell
      width="wide"
      pad="none"
      className="flex flex-1 flex-col gap-16 py-10 sm:gap-20 sm:py-14"
      data-testid="tenant-home"
    >
      {/* Hero — portrait-led, one standfirst paragraph, two destinations. */}
      <section
        className="grid items-center gap-8 md:grid-cols-[1.1fr_0.9fr] md:gap-12"
        data-testid="home-instructor-hero"
      >
        <div className="order-2 md:order-1">
          <Heading as="p" level="eyebrow">
            {tenant.name}
          </Heading>
          <Heading as="h1" level="display" className="mt-3">
            {t("instructorName")}
          </Heading>
          {/* The thesis paragraph, not the credentials sentence. */}
          <Text tone="secondary" size="lg" className="mt-5 max-w-xl">
            {t("bioNotFear")}
          </Text>
          <div className="mt-8 flex flex-wrap gap-3">
            {browseCoursesLink}
            <Link
              to={aboutHref}
              className={buttonClasses({ variant: "secondary", size: "lg", pill: true })}
              data-testid="home-about-instructor"
            >
              {t("aboutInstructor")}
            </Link>
          </div>
        </div>
        <div className="order-1 md:order-2">
          <img
            src={INSTRUCTOR_IMAGES.portrait.src}
            width={INSTRUCTOR_IMAGES.portrait.width}
            height={INSTRUCTOR_IMAGES.portrait.height}
            alt={t("portraitAlt")}
            className="mx-auto w-full max-w-sm rounded-2xl border border-[var(--accent-border)] object-cover shadow-md"
          />
        </div>
      </section>

      {/* What guidance covers — the consultation areas, as the biography lists them. */}
      <section aria-labelledby="home-guidance-title" data-testid="home-guidance">
        <Heading as="h2" id="home-guidance-title" level="section">
          {t("guidanceTitle")}
        </Heading>
        <Text tone="muted" size="base" className="mt-3 max-w-3xl">
          {t("guidanceIntro")}
        </Text>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {GUIDANCE_KEYS.map((key) => (
            <li key={key}>
              <Card
                tone="sunken"
                pad="sm"
                className="h-full border-l-2 border-l-[var(--accent)] pl-4"
              >
                <Text as="span" tone="primary" size="sm" className="font-medium">
                  {t(key)}
                </Text>
              </Card>
            </li>
          ))}
        </ul>
      </section>

      {/* Teaching approach. */}
      <section
        aria-labelledby="home-approach-title"
        data-testid="home-approach"
        className="grid items-center gap-8 md:grid-cols-2 md:gap-12"
      >
        <div>
          <Heading as="h2" id="home-approach-title" level="section">
            {t("approachTitle")}
          </Heading>
          <Text tone="secondary" size="base" className="mt-4">
            {t("bioApproach")}
          </Text>
          <Text tone="secondary" size="base" className="mt-4">
            {t("bioVision")}
          </Text>
        </div>
        <img
          src={INSTRUCTOR_IMAGES.teaching.src}
          width={INSTRUCTOR_IMAGES.teaching.width}
          height={INSTRUCTOR_IMAGES.teaching.height}
          alt={t("teachingAlt")}
          loading="lazy"
          className="w-full rounded-2xl border border-[var(--border)] object-cover shadow-sm"
        />
      </section>

      {/* Philosophy pull-quote. */}
      <section
        aria-labelledby="home-philosophy-title"
        data-testid="home-philosophy"
        className="grid items-center gap-8 md:grid-cols-2 md:gap-12"
      >
        <img
          src={INSTRUCTOR_IMAGES.reflecting.src}
          width={INSTRUCTOR_IMAGES.reflecting.width}
          height={INSTRUCTOR_IMAGES.reflecting.height}
          alt={t("reflectingAlt")}
          loading="lazy"
          className="w-full rounded-2xl border border-[var(--border)] object-cover shadow-sm"
        />
        <div>
          <Heading as="h2" id="home-philosophy-title" level="section">
            {t("philosophyTitle")}
          </Heading>
          <Text tone="muted" size="sm" className="mt-3">
            {t("philosophyIntro")}
          </Text>
          <blockquote className="mt-4 border-l-2 border-l-[var(--accent-strong)] pl-5">
            <Text
              as="p"
              tone="primary"
              size="lg"
              className="font-serif text-xl leading-relaxed text-[var(--text-heading)] sm:text-2xl"
            >
              {t("philosophyQuote")}
            </Text>
          </blockquote>
        </div>
      </section>

      {/* Closing call to action into the catalogue. */}
      <section
        aria-labelledby="home-closing-title"
        data-testid="home-closing"
        className="border-t border-[var(--border)] pt-10 text-center"
      >
        <Heading as="h2" id="home-closing-title" level="section">
          {t("closingTitle")}
        </Heading>
        <Text tone="muted" size="sm" className="mt-3">
          {t("courseCount", { count: courses.length })}
        </Text>
        <div className="mt-6 flex justify-center">
          <Link
            to={coursesHref}
            className={buttonClasses({ variant: "accent", size: "lg", pill: true })}
            data-testid="home-closing-browse-courses"
          >
            {t("browseCourses")}
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
